import { useCallback, useEffect, useState } from 'react'
import api from '@/services/api'

const GESTORES_EVIDENCE_MARKER = '/gestores/equipe/'

/** Normaliza URL salva/exibida — sempre `/gestores/equipe/.../feedback/evidencia/...` */
export function normalizeGestorFeedbackEvidencePath(path: string | null | undefined): string {
  if (!path?.trim()) return ''

  let value = path.trim()

  if (/^https?:\/\//i.test(value)) {
    try {
      value = new URL(value).pathname
    } catch {
      return ''
    }
  }

  if (!value.startsWith('/')) value = `/${value}`

  while (/^\/api\/v1(\/|$)/.test(value)) {
    value = value.replace(/^\/api\/v1\/?/, '/')
    if (value === '/') value = ''
  }

  const idx = value.indexOf(GESTORES_EVIDENCE_MARKER)
  if (idx >= 0) return value.slice(idx)

  return value
}

/** Caminho relativo para `api.get` (baseURL já é `/api/v1`). */
export function toGestorFeedbackEvidenceApiPath(path: string | null | undefined): string {
  return normalizeGestorFeedbackEvidencePath(path)
}

export function useAuthenticatedAssetUrl(path: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    const apiPath = toGestorFeedbackEvidenceApiPath(path)
    if (!apiPath) {
      setUrl(null)
      return
    }

    let active = true
    let objectUrl: string | null = null

    const load = async () => {
      try {
        const response = await api.get(apiPath, { responseType: 'blob' })
        if (!active) return
        objectUrl = URL.createObjectURL(response.data)
        setUrl(objectUrl)
      } catch {
        if (active) setUrl(null)
      }
    }

    void load()

    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path])

  return url
}

export function useLocalFilePreview() {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const setFilePreview = useCallback((file: File | null) => {
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current)
      return file ? URL.createObjectURL(file) : null
    })
  }, [])

  const clearPreview = useCallback(() => setFilePreview(null), [setFilePreview])

  useEffect(
    () => () => {
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current)
        return null
      })
    },
    [],
  )

  return { previewUrl, setFilePreview, clearPreview }
}
